import React, { useState, useMemo, useEffect } from 'react';
import { 
  Users, Search, Plus, MapPin, Phone, ChevronRight, Filter, Download, Settings, X, MoreVertical, Briefcase, Trash2, Upload, FileSpreadsheet, FileDown, AlertTriangle, CheckSquare, Square, Globe, Navigation, ChevronLeft, Tag, Hash, Check, Loader2
} from 'lucide-react';
import { Contact, User, UserRole, Task, Attendance, WhatsAppAssessor, TaskSector } from '../types';
import ContactDetailModal from './ContactDetailModal';
import TaskDetailModal from './TaskDetailModal';
import AttendanceDetailModal from './AttendanceDetailModal';
import ImportContactsModal from './ImportContactsModal';
import BulkGeocodeModal from './BulkGeocodeModal';
import { supabase } from '../supabase';

interface CRMListProps {
  contacts: Contact[];
  categories: string[];
  currentUser: User;
  containerId: string;
  onUpdateContact: (id: string, updates: Partial<Contact>) => void;
  onDeleteContact: (id: string) => Promise<boolean>;
  onOpenCreate: () => void;
  onAddCategory: (cat: string) => void;
  onRemoveCategory: (cat: string) => void;
  onRefresh?: () => void;
  preselectedContactId: string | null;
  onClearPreselected: () => void;
  onBulkDeleteContacts: (ids: string[]) => Promise<void>;
  onBulkAddTags: (ids: string[], tags: string[]) => Promise<void>;
  tasks?: Task[];
  attendances?: Attendance[];
  users?: User[];
  sectors?: TaskSector[];
  waAssessors?: WhatsAppAssessor[];
  onRefreshData?: () => void;
}

const CRMList: React.FC<CRMListProps> = ({
  contacts,
  categories,
  currentUser,
  containerId,
  onUpdateContact,
  onDeleteContact,
  onOpenCreate,
  onAddCategory,
  onRemoveCategory,
  onRefresh,
  preselectedContactId,
  onClearPreselected,
  onBulkDeleteContacts,
  onBulkAddTags,
  tasks = [],
  attendances = [],
  users: allUsers = [],
  sectors = [],
  waAssessors = [],
  onRefreshData
}) => {
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [openAttendanceId, setOpenAttendanceId] = useState<string | null>(null);
  const openTask = useMemo(() => tasks.find(t => t.id === openTaskId) || null, [tasks, openTaskId]);
  const openAttendance = useMemo(() => attendances.find(a => a.id === openAttendanceId) || null, [attendances, openAttendanceId]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkGeocode, setShowBulkGeocode] = useState(false);
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(50);
  
  // Estado para seleção múltipla
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Bulk Tags
  const [showBulkTagsModal, setShowBulkTagsModal] = useState(false);
  const [bulkTagsInput, setBulkTagsInput] = useState('');
  const [isAddingTags, setIsAddingTags] = useState(false);

  const isLeadership = currentUser.role === UserRole.LEADERSHIP;

  useEffect(() => {
    if (preselectedContactId) {
      setSelectedContactId(preselectedContactId);
    }
  }, [preselectedContactId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, itemsPerPage]);

  // Volta para página 1 quando a lista de contatos é atualizada (ex: após criação)
  const prevContactsLengthRef = React.useRef(contacts.length);
  useEffect(() => {
    if (contacts.length > prevContactsLengthRef.current) {
      setCurrentPage(1);
    }
    prevContactsLengthRef.current = contacts.length;
  }, [contacts.length]);

  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (contact.name || '').toLowerCase().includes(term) ||
        (contact.emails || []).some(e => e.toLowerCase().includes(term)) ||
        (contact.phones || []).some(p => p.includes(term)) ||
        (contact.city || '').toLowerCase().includes(term) ||
        (contact.neighborhood || '').toLowerCase().includes(term) ||
        (contact.support_level || '').toLowerCase().includes(term) ||
        (contact.tags || []).some(t => t.toLowerCase().includes(term));

      const matchesCategory = selectedCategory === 'all' || contact.type === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [contacts, searchTerm, selectedCategory]);

  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredContacts.length / itemsPerPage);
  const paginatedContacts = useMemo(() => {
    if (itemsPerPage === 'all') return filteredContacts;
    const start = (currentPage - 1) * itemsPerPage;
    return filteredContacts.slice(start, start + itemsPerPage);
  }, [filteredContacts, currentPage, itemsPerPage]);

  const contactsWithoutGeo = useMemo(() => {
    return contacts.filter(c => !c.latitude && c.street && c.city && !c.deleted_at);
  }, [contacts]);

  const selectedContact = useMemo(() => contacts.find(c => c.id === selectedContactId), [contacts, selectedContactId]);

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredContacts.length && filteredContacts.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContacts.map(c => c.id)));
    }
  };

  const handleExportCSV = () => {
    const contactsToExport = selectedIds.size > 0 
      ? contacts.filter(c => selectedIds.has(c.id))
      : filteredContacts;

    const headers = ['Nome', 'Categoria', 'Nível de Apoio', 'Telefone', 'Email', 'Cidade', 'Bairro', 'Tags', 'Zona', 'Seção'];
    const csvContent = [
      headers.join(','),
      ...contactsToExport.map(c => [
        `"${c.name}"`,
        `"${c.type}"`,
        `"${c.support_level || ''}"`,
        `"${c.phones[0] || ''}"`,
        `"${c.emails[0] || ''}"`,
        `"${c.city || ''}"`,
        `"${c.neighborhood || ''}"`,
        `"${c.tags.join(', ')}"`,
        `"${c.voter_zone || ''}"`,
        `"${c.voter_section || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contatos_crm_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmBulkDelete = async () => {
    await onBulkDeleteContacts(Array.from(selectedIds));
    setSelectedIds(new Set());
    setShowBulkDeleteConfirm(false);
  };

  const handleApplyBulkTags = async () => {
    if (!bulkTagsInput.trim()) return;
    setIsAddingTags(true);
    const tagsToAdd = bulkTagsInput.split(',').map(t => t.trim()).filter(t => t !== '');
    try {
      await onBulkAddTags(Array.from(selectedIds), tagsToAdd);
      setSelectedIds(new Set());
      setBulkTagsInput('');
      setShowBulkTagsModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsAddingTags(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative">
      {showBulkTagsModal && (
        <div className="fixed inset-0 z-[160] bg-navy-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 animate-in fade-in zoom-in-95">
           <div className="w-full max-w-md bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="w-16 h-16 bg-parliament-50 dark:bg-navy-800 rounded-2xl flex items-center justify-center mb-6 text-parliament-600 shadow-inner mx-auto"><Tag size={28} /></div>
              <h3 className="text-2xl font-black text-navy-950 dark:text-white tracking-tighter mb-2 text-center">Etiquetas em Lote</h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium mb-6 text-center text-sm">Adicionar novas etiquetas para {selectedIds.size} contatos selecionados.</p>
              <div className="space-y-2 mb-8">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Digite as Etiquetas (separadas por vírgula)</label>
                 <input 
                   autoFocus
                   value={bulkTagsInput}
                   onChange={e => setBulkTagsInput(e.target.value)}
                   className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600"
                   placeholder="Ex: Liderança, Bairro Centro, Apoiador"
                 />
              </div>
              <div className="flex gap-3">
                 <button onClick={() => setShowBulkTagsModal(false)} className="flex-1 py-4 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-navy-800 rounded-2xl transition-all">Cancelar</button>
                 <button 
                   onClick={handleApplyBulkTags} 
                   disabled={isAddingTags || !bulkTagsInput.trim()}
                   className="flex-1 py-4 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                 >
                    {isAddingTags ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>} Adicionar Tags
                 </button>
              </div>
           </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-[150] bg-navy-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95">
           <div className="w-full max-w-md bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-2xl" onClick={e => e.stopPropagation()}>
             <div className="w-20 h-20 bg-red-500/20 rounded-[24px] flex items-center justify-center mb-6 shadow-xl mx-auto text-red-500"><Trash2 size={32} /></div>
             <h3 className="text-3xl font-black text-navy-950 dark:text-white tracking-tighter mb-2">Excluir {selectedIds.size} Contatos?</h3>
             <p className="text-slate-500 dark:text-slate-400 font-medium mb-8">Esta ação moverá os registros selecionados para a lixeira.</p>
             <div className="flex gap-4 justify-center">
                <button onClick={() => setShowBulkDeleteConfirm(false)} className="px-8 py-3 bg-neutral-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-neutral-200 transition-all">Cancelar</button>
                <button onClick={handleConfirmBulkDelete} className="px-8 py-3 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase shadow-xl hover:scale-105 transition-all">Confirmar</button>
             </div>
           </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
        <div>
          <h2 className="text-4xl font-black text-navy-950 dark:text-white tracking-tighter leading-none">Cidadãos</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">Base de relacionamento e contatos estratégicos.</p>
        </div>
        <div className="flex items-center gap-3">
            {currentUser.permissions.canEditCRM && (
               <>
                {!isLeadership && (
                  <button onClick={() => setShowImportModal(true)} className="p-4 bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 rounded-2xl hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all shadow-sm border border-neutral-200 dark:border-navy-800 flex gap-2 font-bold text-xs items-center" title="Importar Contatos">
                      <Upload size={20} />
                  </button>
                )}
                {!isLeadership && contactsWithoutGeo.length > 0 && (
                  <button onClick={() => setShowBulkGeocode(true)} className="p-4 bg-parliament-50 dark:bg-navy-900 text-parliament-600 dark:text-parliament-400 rounded-2xl hover:bg-parliament-100 dark:hover:bg-navy-800 transition-all shadow-sm border border-parliament-100 dark:border-navy-800 flex gap-2 font-bold text-xs items-center" title="Localizar Contatos no Mapa">
                      <Navigation size={20} />
                      Localizar ({contactsWithoutGeo.length})
                  </button>
                )}
                {!isLeadership && (
                  <>
                    <button onClick={handleExportCSV} className="p-4 bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 rounded-2xl hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all shadow-sm border border-neutral-200 dark:border-navy-800 flex gap-2 font-bold text-xs items-center" title="Exportar CSV">
                        <Download size={20} />
                        {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                    </button>
                    {selectedIds.size > 0 && (
                      <div className="flex items-center gap-2 animate-in slide-in-from-right-4">
                        <button 
                          onClick={() => setShowBulkTagsModal(true)} 
                          className="p-4 bg-parliament-50 dark:bg-parliament-900/20 text-parliament-600 rounded-2xl hover:bg-parliament-100 transition-all shadow-sm border border-parliament-100 dark:border-parliament-900/30 flex gap-2 font-bold text-xs items-center"
                          title="Adicionar Tags"
                        >
                          <Tag size={20} /> Tags
                        </button>
                        <button 
                          onClick={() => setShowBulkDeleteConfirm(true)} 
                          className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/40 transition-all shadow-sm border border-red-100 dark:border-red-900/30 flex gap-2 font-bold text-xs items-center" 
                          title="Excluir Selecionados"
                        >
                            <Trash2 size={20} />
                            Excluir ({selectedIds.size})
                        </button>
                      </div>
                    )}
                  </>
                )}
                <button onClick={onOpenCreate} className="bg-navy-900 dark:bg-parliament-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl flex items-center gap-3 hover:scale-[1.02] active:scale-95 transition-all">
                    <Plus size={20} /> Novo Contato
                </button>
               </>
            )}
        </div>
      </div>

      <div className="bg-white dark:bg-navy-900 rounded-[40px] border border-slate-200 dark:border-navy-800 shadow-sm overflow-hidden mx-2">
         <div className="p-8 border-b border-slate-100 dark:border-navy-800 flex flex-col md:flex-row gap-6 bg-slate-50/50 dark:bg-navy-950/30">
            <div className="relative flex-1 group">
               <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-navy-900 dark:group-focus-within:text-white transition-colors" size={20} />
               <input 
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 placeholder="Buscar por nome, apoio, cidade ou tags..." 
                 className="w-full pl-12 pr-6 py-4 bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-800 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-slate-100 dark:focus:ring-navy-800 outline-none dark:text-white transition-all shadow-sm"
               />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
               <div className="flex items-center gap-2 bg-white dark:bg-navy-900 p-1.5 rounded-2xl border border-slate-200 dark:border-navy-800 shadow-sm">
                  <button 
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${selectedCategory === 'all' ? 'bg-navy-900 dark:bg-white text-white dark:text-navy-950 shadow-md' : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-navy-800'}`}
                  >
                    Todos
                  </button>
                  {categories.map(cat => (
                    <button 
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${selectedCategory === cat ? 'bg-navy-900 dark:bg-white text-white dark:text-navy-950 shadow-md' : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-navy-800'}`}
                    >
                      {cat}
                    </button>
                  ))}
               </div>
            </div>
         </div>

         <div className="overflow-x-auto">
            <table className="w-full text-left">
               <thead>
                  <tr className="bg-white dark:bg-navy-900 text-slate-400 text-[9px] uppercase tracking-[0.2em] font-black border-b border-slate-100 dark:border-navy-800">
                     <th className="px-4 py-6 w-10 text-center">
                        <button
                          onClick={toggleSelectAll}
                          aria-label={selectedIds.size > 0 && selectedIds.size === filteredContacts.length ? 'Desmarcar todos' : 'Selecionar todos'}
                          title={selectedIds.size > 0 && selectedIds.size === filteredContacts.length ? 'Desmarcar todos' : 'Selecionar todos'}
                          className="text-slate-400 hover:text-navy-900 dark:hover:text-white"
                        >
                           {selectedIds.size > 0 && selectedIds.size === filteredContacts.length ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                     </th>
                     <th className="px-4 py-6">Cidadão</th>
                     <th className="px-4 py-6">Status / Categoria</th>
                     <th className="px-4 py-6">Contato</th>
                     <th className="px-4 py-6">Localização</th>
                     <th className="px-8 py-6 text-right pr-10">Ação</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-100 dark:divide-navy-800">
                  {paginatedContacts.map(contact => (
                     <tr key={contact.id} onClick={() => setSelectedContactId(contact.id)} className={`hover:bg-slate-50 dark:hover:bg-navy-950/50 transition-all cursor-pointer group ${selectedIds.has(contact.id) ? 'bg-slate-50 dark:bg-navy-950/30' : ''}`}>
                        <td className="px-4 py-6 text-center" onClick={(e) => e.stopPropagation()}>
                           <button
                             onClick={(e) => toggleSelect(contact.id, e)}
                             aria-label={selectedIds.has(contact.id) ? `Desmarcar ${contact.name}` : `Selecionar ${contact.name}`}
                             title={selectedIds.has(contact.id) ? 'Desmarcar' : 'Selecionar'}
                             className="text-slate-300 hover:text-parliament-600"
                           >
                              {selectedIds.has(contact.id) ? <CheckSquare size={18} className="text-parliament-600" /> : <Square size={18} />}
                           </button>
                        </td>
                        <td className="px-4 py-6">
                           <div className="flex items-center gap-4">
                              <div className="relative w-10 h-10 rounded-2xl overflow-hidden flex-shrink-0">
                                 <div className="w-10 h-10 bg-navy-50 dark:bg-navy-800 text-navy-900 dark:text-white flex items-center justify-center font-black text-sm border border-navy-100 dark:border-navy-700 rounded-2xl">
                                    {contact.name.charAt(0)}
                                 </div>
                                 {contact.photo_url && <img src={contact.photo_url} alt="" className="absolute inset-0 w-10 h-10 rounded-2xl object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                              </div>
                              <div>
                                 <p className="font-bold text-navy-900 dark:text-white text-sm group-hover:text-parliament-600 transition-colors">{contact.name}</p>
                                 <div className="flex gap-2 mt-1">
                                    {contact.tags.slice(0, 2).map(tag => (
                                       <span key={tag} className="text-[8px] px-1.5 py-0.5 bg-slate-100 dark:bg-navy-800 rounded text-slate-500 font-bold">{tag}</span>
                                    ))}
                                 </div>
                              </div>
                           </div>
                        </td>
                        <td className="px-4 py-6">
                           <div className="flex flex-col gap-1 items-start">
                              <span className="px-3 py-1 bg-slate-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-lg text-[9px] font-black uppercase tracking-widest border border-slate-200 dark:border-navy-700">
                                 {contact.type}
                              </span>
                              {contact.support_level && (
                                 <span className="px-2 py-0.5 text-[8px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 rounded uppercase tracking-wider">
                                    {contact.support_level}
                                 </span>
                              )}
                           </div>
                        </td>
                        <td className="px-4 py-6">
                           <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-400">
                                 <Phone size={12} className="text-slate-300" /> {contact.phones[0] || '-'}
                              </div>
                           </div>
                        </td>
                        <td className="px-4 py-6">
                           <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-400">
                              <MapPin size={12} className={contact.latitude ? 'text-emerald-500' : 'text-slate-300'} /> 
                              {contact.city || '-'}
                              {contact.latitude && (
                                <div className="p-0.5 bg-emerald-100 dark:bg-emerald-900/30 rounded" title="Geolocalizado">
                                    <Globe size={10} className="text-emerald-600" />
                                </div>
                              )}
                           </div>
                        </td>
                        <td className="px-8 py-6 text-right pr-10">
                           <button aria-label={`Ver detalhes de ${contact.name}`} className="p-2 text-slate-300 group-hover:text-parliament-600 transition-colors">
                              <ChevronRight size={20} />
                           </button>
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
            {paginatedContacts.length === 0 && (
               <div className="py-24 text-center">
                  <div className="w-20 h-20 bg-slate-50 dark:bg-navy-800 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-300">
                     <Users size={32} />
                  </div>
                  <p className="text-slate-400 font-bold text-sm">Nenhum contato encontrado.</p>
               </div>
            )}
         </div>

         <div className="p-6 border-t border-slate-100 dark:border-navy-800 bg-slate-50/30 dark:bg-navy-950/30 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
               <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Visualizar:</span>
                  <div className="flex bg-white dark:bg-navy-900 p-1 rounded-xl border border-slate-200 dark:border-navy-800 shadow-sm">
                     {[50, 100, 'all'].map(val => (
                        <button 
                           key={val} 
                           onClick={() => setItemsPerPage(val as any)}
                           className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${itemsPerPage === val ? 'bg-navy-900 text-white dark:bg-white dark:text-navy-900 shadow-md' : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-navy-800'}`}
                        >
                           {val === 'all' ? 'Tudo' : val}
                        </button>
                     ))}
                  </div>
               </div>
               <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {filteredContacts.length} contatos encontrados
               </p>
            </div>

            {itemsPerPage !== 'all' && totalPages > 1 && (
               <div className="flex items-center gap-2">
                  <button 
                     disabled={currentPage === 1}
                     onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                     className="p-2 rounded-xl bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-800 text-slate-500 disabled:opacity-30 hover:bg-slate-50 transition-all shadow-sm"
                  >
                     <ChevronLeft size={18} />
                  </button>
                  <div className="flex items-center gap-1">
                     {[...Array(totalPages)].map((_, i) => {
                        const pg = i + 1;
                        if (pg === 1 || pg === totalPages || (pg >= currentPage - 1 && pg <= currentPage + 1)) {
                           return (
                              <button 
                                 key={pg}
                                 onClick={() => setCurrentPage(pg)}
                                 className={`w-8 h-8 rounded-xl text-[10px] font-black transition-all ${currentPage === pg ? 'bg-navy-900 text-white dark:bg-white dark:text-navy-900 shadow-md' : 'text-slate-400 hover:bg-white dark:hover:bg-navy-800'}`}
                              >
                                 {pg}
                              </button>
                           );
                        }
                        if (pg === currentPage - 2 || pg === currentPage + 2) return <span key={pg} className="text-slate-300">...</span>;
                        return null;
                     })}
                  </div>
                  <button 
                     disabled={currentPage === totalPages}
                     onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                     className="p-2 rounded-xl bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-800 text-slate-500 disabled:opacity-30 hover:bg-slate-50 transition-all shadow-sm"
                  >
                     <ChevronRight size={18} />
                  </button>
               </div>
            )}
         </div>
      </div>

      {selectedContact && (
        <ContactDetailModal
           contact={selectedContact}
           categories={categories}
           currentUser={currentUser}
           tasks={tasks}
           attendances={attendances}
           onOpenTask={(id) => { setSelectedContactId(null); setOpenTaskId(id); }}
           onOpenAttendance={(id) => { setSelectedContactId(null); setOpenAttendanceId(id); }}
           onClose={() => { setSelectedContactId(null); onClearPreselected(); }}
           onUpdateContact={onUpdateContact}
           onDeleteContact={onDeleteContact}
        />
      )}

      {openTask && (
        <TaskDetailModal
          task={openTask}
          currentUser={currentUser}
          users={allUsers}
          waAssessors={waAssessors}
          contacts={contacts}
          sectors={sectors}
          onClose={() => setOpenTaskId(null)}
          onUpdateTask={async (id, up) => { await supabase.from('tasks').update(up).eq('id', id); onRefreshData?.(); }}
          onDeleteTask={async (id) => { await supabase.from('tasks').update({ deleted_at: new Date().toISOString() }).eq('id', id); onRefreshData?.(); return true; }}
        />
      )}

      {openAttendance && (
        <AttendanceDetailModal
          attendance={openAttendance}
          users={allUsers}
          contacts={contacts}
          currentUser={currentUser}
          onClose={() => setOpenAttendanceId(null)}
          onUpdateAttendance={async (id, up) => { await supabase.from('attendances').update(up).eq('id', id); onRefreshData?.(); }}
          onDeleteAttendance={async (id) => { await supabase.from('attendances').update({ deleted_at: new Date().toISOString() }).eq('id', id); onRefreshData?.(); return true; }}
        />
      )}

      {showImportModal && (
         <ImportContactsModal
            onClose={() => setShowImportModal(false)}
            currentUser={currentUser}
            containerId={containerId}
            categories={categories}
         />
      )}

      {showBulkGeocode && (
        <BulkGeocodeModal
           contacts={contactsWithoutGeo}
           onClose={() => setShowBulkGeocode(false)}
           onComplete={() => {
              setShowBulkGeocode(false);
              if (onRefresh) onRefresh();
           }}
        />
      )}
    </div>
  );
};

export default CRMList;